import type { FunctionComponent } from "react";
import { IconCheckRadio, IconException, IconWarning, IconInfoCircle, IIcon } from "@geotab/zenith";
import type { TPillType } from "@geotab/zenith/dist/utils/pillUtils/getPillTypeClassName";
import { InspectionsTranslationsDictionary } from "./translations";
import { InspectedItemStatus } from "../store/types";

export const SEVERITY_MAJOR = "major";
export const SEVERITY_MINOR = "minor";
export const SEVERITY_COSMETIC = "cosmetic";
// An open defect on an unregulated item (MYG-151881) - never selectable, see SEVERITY_PRIORITY below.
export const SEVERITY_NOT_APPLICABLE = "notApplicable";
export const SEVERITY_NONE = "none";

export type SeverityType = "major" | "minor" | "cosmetic" | "notApplicable" | "none";

interface ISeverityConfig {
    pillType: TPillType;
    icon: FunctionComponent<IIcon>;
    translationKey: string;
}

export const SEVERITY_CONFIG: Record<SeverityType, ISeverityConfig> = {
    [SEVERITY_MAJOR]: { pillType: "error", icon: IconException, translationKey: InspectionsTranslationsDictionary.Major },
    [SEVERITY_MINOR]: { pillType: "warning", icon: IconWarning, translationKey: InspectionsTranslationsDictionary.Minor },
    [SEVERITY_COSMETIC]: { pillType: "info", icon: IconInfoCircle, translationKey: InspectionsTranslationsDictionary.Cosmetic },
    // "default" (not "info"), so the summary pill reads distinctly from Cosmetic at a glance.
    [SEVERITY_NOT_APPLICABLE]: { pillType: "default", icon: IconInfoCircle, translationKey: InspectionsTranslationsDictionary.NotApplicable },
    [SEVERITY_NONE]: { pillType: "success", icon: IconCheckRadio, translationKey: InspectionsTranslationsDictionary.NoDefects }
};

// Selectable severities only - drives inspectionItemCard's driver-facing picker. Must exclude
// SEVERITY_NOT_APPLICABLE, which is derived, never chosen (MYG-151881).
export const SEVERITY_PRIORITY: SeverityType[] = [SEVERITY_MAJOR, SEVERITY_MINOR, SEVERITY_COSMETIC];

// Summary-pill display order only (defectSeverityPill.tsx) - N/A belongs here, not above.
export const SEVERITY_PILL_ORDER: SeverityType[] = [...SEVERITY_PRIORITY, SEVERITY_NOT_APPLICABLE];

export function getSeverityConfig(severity?: string): ISeverityConfig | undefined {
    const key = severity?.toLowerCase() as SeverityType;
    return SEVERITY_CONFIG[key];
}

/**
 * Item statuses of an open defect. Mirrors the server's
 * InspectedItemStatusRules.OpenDefectStatuses and MyGeotab's OPEN_DEFECT_ITEM_STATUSES:
 * NotRepaired and InProgress are the consolidated open statuses, Reported is what a new
 * defect is still recorded as, and the retired Monitoring is kept so a row the data
 * migration has not reached yet reads the same as a migrated one (Monitoring → InProgress).
 */
export const OPEN_DEFECT_ITEM_STATUSES = ["NotRepaired", "InProgress", "Reported", "Monitoring"] as const satisfies readonly InspectedItemStatus[];

export type OpenDefectItemStatus = typeof OPEN_DEFECT_ITEM_STATUSES[number];

const OPEN_DEFECT_ITEM_STATUS_SET = new Set<string>(OPEN_DEFECT_ITEM_STATUSES);

/**
 * Active-defect status. Once a defect leaves the open set (Repaired, RepairNotNecessary,
 * Closed, ...) it no longer counts as an active defect and repair status must not be
 * surfaced for it. Mirrors isDefectStatus in MyGeotab's assetInspectionDetail page
 * (statsPanel.tsx). Pill colour is a separate, narrower rule (getStatusPillType: red for
 * Reported only); review both when the open-defect set changes.
 */
export function isDefectStatus(itemStatus?: string): itemStatus is OpenDefectItemStatus {
    return itemStatus !== undefined && OPEN_DEFECT_ITEM_STATUS_SET.has(itemStatus);
}

/**
 * Full-lifecycle defect check (MYG-151881): true once a defect has ever been recorded against an
 * item, unlike isDefectStatus above which only covers the open subset. Mirrors isRecordedDefect in
 * MyGeotab's inspectedItemsList.tsx - only Pass and Unknown are excluded, since neither ever had a
 * defect recorded in the first place.
 */
export function wasEverADefect(itemStatus?: string): boolean {
    return itemStatus !== undefined && itemStatus !== "Pass" && itemStatus !== "Unknown";
}

/**
 * Maps an item's lifecycle status to the appropriate Zenith Pill type. Red is reserved for a
 * freshly recorded (Reported) defect; open statuses part-way through the lifecycle (NotRepaired,
 * InProgress, Monitoring) stay neutral even though isDefectStatus counts them — mirrors
 * getStatusPillType in MyGeotab's inspectedItemsList.tsx. Deliberately not derived
 * from OPEN_DEFECT_ITEM_STATUSES; review both when the open-defect set changes.
 */
export function getStatusPillType(itemStatus?: string): TPillType {
    if (itemStatus === "Pass") { return "success"; }
    if (itemStatus === "Reported") { return "error"; }
    return "default";
}

/**
 * Roadside classification of a defect by severity.
 *
 * Orthogonal to utils/regulatoryInspection.isRegulatoryInspection: this classifies a defect
 * *within* an already-regulatory inspection and is not a term in that predicate.
 * Major and Minor map to the regulated defect classes of a regulatory inspection;
 * Cosmetic is a non-regulatory observation. Returns undefined for unknown/absent
 * severities so callers can omit the classification instead of guessing.
 */
export function isRegulatorySeverity(severity?: string): boolean | undefined {
    const key = severity?.toLowerCase();
    if (key === SEVERITY_MAJOR || key === SEVERITY_MINOR) {
        return true;
    }
    if (key === SEVERITY_COSMETIC) {
        return false;
    }
    return undefined;
}

// Repair status config - values match RepairStatusType enum from API
export type RepairStatusType = "NotRepaired" | "Repaired" | "NotNecessary" | "RepairDisputed";

interface IRepairStatusConfig {
    pillType: TPillType;
    translationKey: string;
}

const REPAIR_STATUS_CONFIG: Record<RepairStatusType, IRepairStatusConfig> = {
    NotRepaired: { pillType: "error", translationKey: InspectionsTranslationsDictionary.NotRepaired },
    Repaired: { pillType: "success", translationKey: InspectionsTranslationsDictionary.Repaired },
    NotNecessary: { pillType: "info", translationKey: InspectionsTranslationsDictionary.NotNecessary },
    // Outstanding again after a driver rejected the repair — same "error" treatment as NotRepaired.
    RepairDisputed: { pillType: "error", translationKey: InspectionsTranslationsDictionary.RepairDisputed }
};

export function getRepairStatusConfig(repairStatus?: string): IRepairStatusConfig | undefined {
    return REPAIR_STATUS_CONFIG[repairStatus as RepairStatusType];
}

// Status pill labels for every InspectedItemStatus value. Typed as Record<InspectedItemStatus, string>
// so the compiler enforces exhaustive coverage when new enum members are added to the type.
const ITEM_STATUS_LABELS: Record<InspectedItemStatus, string> = {
    Pass: InspectionsTranslationsDictionary.Pass,
    Fail: InspectionsTranslationsDictionary.Fail,
    Reported: InspectionsTranslationsDictionary.Reported,
    InProgress: InspectionsTranslationsDictionary.InProgress,
    Closed: InspectionsTranslationsDictionary.Closed,
    Monitoring: InspectionsTranslationsDictionary.Monitoring,
    Repaired: InspectionsTranslationsDictionary.Repaired,
    NotRepaired: InspectionsTranslationsDictionary.NotRepaired,
    RepairNotNecessary: InspectionsTranslationsDictionary.RepairNotNecessary,
    Invalid: InspectionsTranslationsDictionary.Invalid,
    Unknown: InspectionsTranslationsDictionary.Unknown
};

/**
 * Translatable label for a known item status. Returns undefined for unrecognized API values
 * so callers can fall back to the raw string without routing it through translate().
 */
export function getItemStatusLabel(itemStatus?: string): string | undefined {
    if (!itemStatus) { return undefined; }
    return ITEM_STATUS_LABELS[itemStatus as InspectedItemStatus];
}
